import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { CookiesService } from '@core/services/cookies/cookies.service';
import { environment } from '../../../../environments/environment';
import { IAuthModel } from '@shared/models/auth.model';
import { MessageResponse, Response } from '@shared/models/response.model';
import { UserModel } from '@shared/models/user.model';
import {
  SynchronizeResponse,
  UnLinkFailedResponse,
  UnLinkSuccessResponse,
} from '@shared/models/contact.model';
import {
  SignupSession,
  SignUpSessionResponse,
} from '../../../@features/authentication/sign-up/interfaces/sign-up.interface';
import { AUTH_REQUIRED } from '@core/tokens/auth-required.token';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public isAuthenticated$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );

  constructor(
    private httpClient: HttpClient,
    private cookiesService: CookiesService
  ) {}

  public createSignUpSession(
    data: SignupSession
  ): Observable<Response<SignUpSessionResponse>> {
    return this.httpClient.post<Response<SignUpSessionResponse>>(
      `${environment.apiUrl}/auth/signup_session`,
      data
    );
  }

  public signUp(code: string, sessionId: string): Observable<UserModel> {
    return this.httpClient.post<UserModel>(
      `${environment.apiUrl}/auth/signup`,
      {
        code,
        session_identificator: sessionId,
      },
      {
        withCredentials: true,
      }
    );
  }

  public signIn(
    login_mail: string,
    password: string,
    type: string
  ): Observable<UserModel> {
    return this.httpClient.post<UserModel>(
      `${environment.apiUrl}/auth/login`,
      {
        login_mail,
        password,
        type,
      },
      {
        withCredentials: true,
      }
    );
  }

  public forgotPassword(
    email: string,
    language: string
  ): Observable<Response<{ message: string }>> {
    return this.httpClient.post<Response<{ message: string }>>(
      `${environment.apiUrl}/auth/forgot_password`,
      {
        email,
        language,
      }
    );
  }

  public resetPassword(
    password: string,
    token_id: string,
    user_id: string,
    language: string
  ): Observable<Response<{ message: string }>> {
    return this.httpClient.post<Response<{ message: string }>>(
      `${environment.apiUrl}/auth/reset_password`,
      {
        password,
        token_id,
        user_id,
        language,
      }
    );
  }

  public getAuthData(): IAuthModel {
    return JSON.parse(this.cookiesService.getCookie('auth_data'));
  }

  public refreshAuthToken(): Observable<any> {
    return this.httpClient.post(`${environment.apiUrl}/auth/refresh`, {
      refresh_token: this.getAuthData().refresh_token,
      public_id: this.getAuthData().public_id,
      withCredentials: true,
    });
  }

  public signOut(): void {
    this.cookiesService.deleteCookie('auth_data');
    this.isAuthenticated$.next(false);
    window.location.reload();
  }

  public exboAuth(code: string): Observable<MessageResponse<UserModel>> {
    return this.httpClient.post<MessageResponse<UserModel>>(
      `${environment.apiUrl}/exbo_auth`,
      {
        code,
      },
      { context: new HttpContext().set(AUTH_REQUIRED, true) }
    );
  }

  public discordAuth(code: string): Observable<MessageResponse<UserModel>> {
    return this.httpClient.post<MessageResponse<UserModel>>(
      `${environment.apiUrl}/discord_auth`,
      {
        code,
      },
      { context: new HttpContext().set(AUTH_REQUIRED, true) }
    );
  }

  public unLinkDiscordAuth(): Observable<
    Response<UnLinkSuccessResponse | UnLinkFailedResponse>
  > {
    return this.httpClient.delete<
      Response<UnLinkSuccessResponse | UnLinkFailedResponse>
    >(`${environment.apiUrl}/discord_auth`, {
      context: new HttpContext().set(AUTH_REQUIRED, true),
    });
  }

  public unLinkExboAuth(): Observable<
    Response<UnLinkSuccessResponse | UnLinkFailedResponse>
  > {
    return this.httpClient.delete<
      Response<UnLinkSuccessResponse | UnLinkFailedResponse>
    >(`${environment.apiUrl}/exbo_auth`, {
      context: new HttpContext().set(AUTH_REQUIRED, true),
    });
  }
  public onSynchronizeExboCharacters(): Observable<
    Response<SynchronizeResponse>
  > {
    return this.httpClient.post<
      Response<SynchronizeResponse>
    >(`${environment.apiUrl}/exbo_auth/synchronize`, {}, {
      context: new HttpContext().set(AUTH_REQUIRED, true),
    });
  }
}
